import React, { useEffect, useState } from "react";
import { Cart, CartItem } from "../../store/cart/types";
import { BusinessItem } from "../../store/page/types";
import { Box, Button, Checkbox, Flex, Heading, IconButton, Stack, Text } from "@chakra-ui/react";
import LoadingPage from "../../pages/loading/LoadingPage";
import { Image } from "@chakra-ui/image";
import { formatCurrency } from "../global/data/formatCurrencyHelper";
import { Link } from "react-router-dom";
import QuantityForm from "../page/QuantityForm";
import { AddIcon, DeleteIcon, MinusIcon } from "@chakra-ui/icons";

interface CartItemProps {
  cart: Cart;
  cartItem: CartItem;
  itemReferences: BusinessItem[];
  asyncRemoveItemFromCart: (cartItemId: string) => void;
  asyncUpdateItemInCart: (cartItemId: string, quantity: number, price: number, currency: string, data?: any) => void;
  updateCartSuccess: (cart: Cart) => void;
  showToast: (toast: string) => void;
}

const CartItemCard: React.FC<CartItemProps> = ({ 
  cart, cartItem, itemReferences, 
  asyncRemoveItemFromCart, asyncUpdateItemInCart, showToast, updateCartSuccess 
}) => {
  const [item, setItem] = useState<BusinessItem | null>(null);
  let image: any = null;
  if (item && item.pageData && item.pageData.media && item.pageData.media.length > 0) {
    image = item.pageData.media[0];
  }
  useEffect(() => {
    const referenceItem = itemReferences.find(obj => obj._id === cartItem.itemId);
    if (referenceItem) {
      setItem(referenceItem);
    }
  }, [itemReferences])

  const toggleItemSelect = () => {
    let newItems: CartItem[] = [];
    if (cart.items) {
      newItems = cart.items.map(cItem => {
        let newCartItem = {...cItem}
        if (newCartItem._id === cartItem._id && cartItem.data.inStock) {
          newCartItem.selected = !cartItem.selected;
        }
        return newCartItem
      })
    }
    const newCart = {...cart, items: newItems };
    updateCartSuccess(newCart);
  }

  const renderItem = () => {
    if (item && item.pageData) {
      const inStock = item.pageData.quantityOnHand && item.pageData.quantityOnHand >= cartItem.quantity;
      const allowBackOrder = item.pageData.allowBackOrder;
      return (
        <Flex flexDirection={'row'} justifyContent={'space-between'}>
          <Box>
         <Flex 
            flexDirection={'row'}
          >
            <Box
              rounded={'xl'}
              height={'175px'}
              _after={{
                transition: 'all .3s ease',
                content: '""',
                w: 'full',
                h: 'full',
                pos: 'absolute',
                left: 0,
                filter: 'blur(5px)',
                zIndex: -1,
              }}
              _groupHover={{
                _after: {
                  filter: 'blur(10px)',
                },
              }}
            >
              <Flex flexDirection={'row'} alignItems={'center'}>
                <Checkbox mr={1} isChecked={cartItem.selected} isDisabled={!inStock && !allowBackOrder} colorScheme={'orange'} onChange={(e) => toggleItemSelect()}/>
                <Link to={`/${item.pageKey}`}>
                  <Image
                    rounded={'lg'}
                    height={150}
                    width={184}
                    objectFit={'cover'}
                    src={image ? image.url : undefined}
                    alt="#"
                  />
                </Link>
              </Flex>
            </Box>
            <Stack align={'flex-start'} ml={2} mt={4}>
              <Heading fontSize={'md'} fontFamily={'body'} fontWeight={500}>
                {item.pageData.title.length > 60 ? `${item.pageData.title.slice(0,60)}...` : item.name}
              </Heading>
              <Flex mt={1} mb={1} flexDirection={'row'} align={'center'}>
                <Text fontWeight={'bold'} fontSize={'sm'} color={'gray.600'}>Quantity: </Text>
                <QuantityForm 
                  quantity={cartItem.quantity} 
                  hideIcon={true}
                  title={''}
                  minLimit={item.pageData.quantityOrderLimitMin}
                  maxLimit={item.pageData.quantityOrderLimitMax}
                  onChange={(quantity: number) => {
                    asyncUpdateItemInCart(cartItem._id, quantity, item.price, item.currency)
                  }}
                />
                <IconButton 
                  ml={1}
                  isDisabled={cartItem.quantity >= item.pageData.quantityOrderLimitMax || cartItem.quantity >= item.pageData.quantityOnHand}
                  borderRadius={'full'}
                  icon={<AddIcon />}
                  aria-label="Add"
                  variant={'outline'}
                  size={'xs'}
                  onClick={() => {
                    asyncUpdateItemInCart(cartItem._id, cartItem.quantity + 1, item.price, item.currency)
                  }}
                />
                <IconButton 
                  mr={1}
                  isDisabled={cartItem.quantity <= item.pageData.quantityOrderLimitMin}
                  borderRadius={'full'}
                  icon={<MinusIcon />}
                  aria-label="Minus"
                  variant={'outline'}
                  size={'xs'}
                  onClick={() => {
                    asyncUpdateItemInCart(cartItem._id, cartItem.quantity - 1, item.price, item.currency)
                  }}
                />
                <Text fontWeight={'bold'} fontSize={'sm'} color={'gray.300'}>|</Text>
                <IconButton 
                  icon={<DeleteIcon />}
                  aria-label="Delete"
                  variant={'ghost'}
                  size={'xs'}
                  onClick={() => {
                    asyncRemoveItemFromCart(cartItem._id)
                    showToast('Removed item from your cart')
                  }}
                />
              </Flex>
            </Stack>
          </Flex>
          </Box>
          <Box mt={5} textAlign={'right'}>
            <Text fontSize={'large'} color={'gray.600'} fontWeight={'bold'} textAlign={'right'}>{formatCurrency(item.currency, item.price * cartItem.quantity)}</Text>
            {cartItem.quantity !== 1 
            ?
            <Text fontSize={'small'} color={'gray.700'} textAlign={'right'}>{cartItem.quantity} x {formatCurrency(item.currency, item.price)} each</Text>
            : null}
            <Button size={'xs'} colorScheme={inStock ? 'green' : 'red'} >
              {inStock ? 'IN STOCK' : 'OUT OF STOCK'}
            </Button>
          </Box>
        </Flex>
      )    
    }
    return null;

  }

  return (
    <Box
      role={'group'}
      p={6}
      w={'full'}
      bg={'white'}
      boxShadow={'md'}
      rounded={'lg'}
      pos={'relative'}
      mb={3}
      zIndex={1}>
      {item ?
      <>
        {renderItem()}
      </>
      
      :
      <LoadingPage loadingText="Loading Item" />
      }
    </Box>
  );
}

export default CartItemCard;